import { isOrderScriptSent } from "@nexus/contracts"
import { Icon, cn } from "@nexus/ui"
import type { MouseEvent as ReactMouseEvent } from "react"
import { useTranslation } from "react-i18next"
import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types"
import { getRevealHref } from "@/lib/navigation/navigate-and-reveal"

export type SentStatusKind = "sent" | "sending" | "failed"

/** SENT is success. PENDING_SEND / SENDING are in-flight. FAILED needs retry. */
export function getSentStatusKind(status: SentScriptMeta["status"]): SentStatusKind {
  if (status === "FAILED") return "failed"
  if (isOrderScriptSent({ status })) return "sent"
  return "sending"
}

function formatAbsolute(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

export interface ScriptSentToVendorCardProps {
  sentMeta: SentScriptMeta
  /** When provided, the whole card becomes a link that opens the order. */
  onOpenOrder?: (event: ReactMouseEvent<HTMLAnchorElement>) => void
  /** Override when the order is available in an embedded workspace. */
  orderHref?: string
  className?: string
}

export function ScriptSentToVendorCard({
  sentMeta,
  onOpenOrder,
  orderHref: orderHrefOverride,
  className,
}: ScriptSentToVendorCardProps) {
  const { t } = useTranslation()
  const kind = getSentStatusKind(sentMeta.status)
  const failed = kind === "failed"
  const sending = kind === "sending"
  const interactive = !!onOpenOrder
  const orderHref =
    orderHrefOverride ??
    getRevealHref({
      route: "/marketplace",
      targetId: sentMeta.orderScriptId ?? sentMeta.orderId,
      expandPath: [sentMeta.orderId],
      search: {
        tab: "in-production",
        order: sentMeta.orderId,
        script: sentMeta.orderScriptId,
      },
    })

  const titlePrefix = t(`opportunities.scripts.sentCard.${kind}.title`)
  const timestamp = formatAbsolute(sentMeta.sentAt)
  const rootClassName = cn(
    "group/sentcard mt-3 flex w-full items-center gap-3 rounded-lg border px-4 py-3.5 text-left shadow-sm transition-shadow",
    failed
      ? "border-destructive/30 bg-destructive/[0.05]"
      : sending
        ? "border-warning/30 bg-warning/[0.05]"
        : "border-success/30 bg-card",
    interactive &&
      "cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
    className,
  )
  const content = (
    <>
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
          failed
            ? "bg-destructive/10 text-destructive"
            : sending
              ? "bg-warning/10 text-warning"
              : "bg-success/10 text-success",
        )}
      >
        <Icon
          name={failed ? "x-circle" : sending ? "loader" : "check-circle"}
          size="sm"
          className={sending ? "animate-spin text-current" : "text-current"}
          aria-hidden
        />
      </span>

      <div className="min-w-0 flex-1">
        <span className="block text-sm leading-snug">
          <span className="text-muted-foreground">{titlePrefix}</span>{" "}
          <span className="font-semibold text-foreground">{sentMeta.vendorName}</span>
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {!failed && sentMeta.sentByUserName && (
            <>
              {t("opportunities.scripts.sentCard.sentBy")}{" "}
              <span className="text-foreground/80">{sentMeta.sentByUserName}</span>
              <span aria-hidden> · </span>
            </>
          )}
          {failed && (
            <>
              {t("opportunities.scripts.sentCard.attemptedOn")}
              <span aria-hidden> · </span>
            </>
          )}
          <time dateTime={sentMeta.sentAt} className="tabular-nums">
            {timestamp}
          </time>
          {failed && (
            <>
              <span aria-hidden> · </span>
              <span className="text-destructive/80">
                {t("opportunities.scripts.sentCard.failed.note")}
              </span>
            </>
          )}
        </span>
      </div>

      {interactive && (
        <span className="ml-auto inline-flex shrink-0 items-center gap-1 self-center rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors group-hover/sentcard:bg-muted">
          {t("opportunities.scripts.sentCard.viewOrder")}
          <Icon name="chevron-right" size="xs" className="text-current" aria-hidden />
        </span>
      )}
    </>
  )

  if (interactive) {
    return (
      <a
        href={orderHref}
        onClick={onOpenOrder}
        aria-label={t("opportunities.scripts.sentCard.viewOrderAria")}
        className={rootClassName}
        data-testid="script-sent-to-vendor-card"
        data-status={kind}
      >
        {content}
      </a>
    )
  }

  return (
    <div
      role="status"
      className={rootClassName}
      data-testid="script-sent-to-vendor-card"
      data-status={kind}
    >
      {content}
    </div>
  )
}
